import { useEffect, useRef, useState } from 'react'

/**
 * Bloc masqué qui n'apparaît qu'au clic sur un lien qui y mène (« Nous
 * écrire », « Remplir le bulletin d'adhésion »…) ou à l'arrivée sur la page
 * avec son ancre. Une fois affiché et prêt (`ready`), il est amené en haut de
 * l'écran, sous l'en-tête — saut direct, qu'aucun défilement en cours ne
 * vienne interrompre. `close` le masque et revient au lien d'origine.
 */
export function useRevealOnLink(id: string, enabled: boolean, ready = true) {
  const [open, setOpen] = useState(false)
  const trigger = useRef<HTMLElement | null>(null)
  const pendingScroll = useRef(false)
  useEffect(() => {
    if (!enabled) return
    if (window.location.hash === `#${id}`) {
      pendingScroll.current = true
      setOpen(true)
    }
    const onClick = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.('a')
      if (!a?.getAttribute('href')?.endsWith(`#${id}`)) return
      trigger.current = a
      pendingScroll.current = true
      setOpen(true)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [id, enabled])
  useEffect(() => {
    if (!open || !ready || !pendingScroll.current) return
    pendingScroll.current = false
    requestAnimationFrame(() => {
      const el = document.getElementById(id)
      if (el)
        window.scrollTo({
          top: el.getBoundingClientRect().top + window.scrollY - 80,
          behavior: 'instant',
        })
    })
  }, [open, ready, id])
  const close = () => {
    setOpen(false)
    setTimeout(() => {
      trigger.current?.scrollIntoView({ block: 'center' })
      trigger.current?.focus()
    }, 0)
  }
  return { open, close }
}
